<template>
  <div class="performance-container bg-[#00554A]" @scroll="handleScroll" ref="containerRef">
    <!-- 顶部搜索栏 -->
    <div class="search-bar">
      <div class="date-range-picker" @click="showDatePicker = true">
        <span>{{ formatDateRange() }}</span>
        <img :src="getAssetsFile('home/date@2x.png')" alt="" class="w-[16px] h-[16px] ml-[5px]">
      </div>
      <!-- 添加清空图标 -->
      <div v-if="startDate || endDate" class="clear-date-btn" @click="clearDateRange">
        <svg width="16" height="16" viewBox="0 0 24 24">
          <path fill="#5FB589"
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </div>
    </div>

    <!-- 表格 -->
    <div class="table-wrap mt-[10px]">
      <div class="table-header">
        <div class="th">{{ $t('performance.period') }}</div>
        <div class="th">{{ $t('performance.promoterLevel') }}</div>
        <div class="th">{{ $t('performance.type') }}</div>
        <div class="th">{{ $t('performance.amount') }}</div>
      </div>
      <template v-if="tableData.length">
        <div v-for="(row, idx) in tableData" :key="row.id" :class="['table-row', { zebra: idx % 2 === 1 }]">
          <div class="td">{{ formatPeriod(row.startTime, row.endTime) }}</div>
          <div class="td">{{ row.promoterLevel }}</div>
          <div class="td">{{ getTypeText(row.type) }}</div>
          <div class="td" :class="{ 'clickable': row.startTime && row.endTime }" style="color:#FEF64A;"
            @click="row.startTime && row.endTime ? handleClick(row) : null">
            {{ VITE_symbol }} {{ row.amount }}
            <img v-if="row.startTime && row.endTime" :src="getAssetsFile('activity/rights.png')" alt=""
              class="w-[7px] h-[10px] ml-[5px]">
          </div>
        </div>
      </template>
      <template v-else>
        <div class="empty-wrap">
          <div class="flex justify-center items-center">
            <img :src="getAssetsFile('home/No_record@2x.png')" alt=""  class="max-h-[84px] max-w-[106.5px] w-auto h-auto">
          </div>
          <div class="empty-text">{{ $t('performance.noRecords') }}</div>
        </div>
      </template>
    </div>

    <!-- 日期范围选择弹窗 -->
    <div v-if="showDatePicker" class="date-picker-modal" @click.self="showDatePicker = false">
      <div class="date-picker-panel">
        <!-- 选择模式切换 -->
        <div class="mode-switch">
          <div :class="['mode-btn', { active: !isSelectingEndDate }]" @click="switchToStartDate">
            {{ $t('performance.startDate') }}
          </div>
          <div :class="['mode-btn', { active: isSelectingEndDate }]" @click="switchToEndDate">
            {{ $t('performance.endDate') }}
          </div>
        </div>

        <!-- 年月选择 -->
        <div class="panel-header">
          <span class="month-year" @click="showMonthSelect = true">
            {{ getMonthName(viewMonth) }} {{ $t('performance.months.of') }} {{ viewYear }}
            <svg width="16" height="16" viewBox="0 0 24 24" style="vertical-align: middle;">
              <path fill="#5FB589" d="M7 10l5 5 5-5z" />
            </svg>
          </span>
          <div class="panel-arrows">
            <svg @click="prevMonth" :class="{ disabled: isPrevDisabled }" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#5FB589" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
            <svg @click="nextMonth" :class="{ disabled: isNextDisabled }" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#5FB589" d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
            </svg>
          </div>
        </div>

        <!-- 月份选择 -->
        <div v-if="showMonthSelect" class="month-select">
          <div class="month-select-header" @click="showMonthSelect = false">
            <svg width="16" height="16" viewBox="0 0 24 24" style="vertical-align: middle;transform:rotate(90deg)">
              <path fill="#5FB589" d="M7 10l5 5 5-5z" />
            </svg>
            <span style="margin-left: 8px;">{{ viewYear }}</span>
          </div>
          <div class="month-list">
            <div v-for="(m, idx) in monthNames" :key="m"
              :class="['month-item', { active: idx === viewMonth, disabled: isFutureMonth(viewYear, idx) }]"
              @click="selectMonth(idx)">
              {{ getMonthName(idx) }}
            </div>
          </div>
        </div>

        <!-- 日历 -->
        <div v-else class="calendar">
          <div class="weekdays">
            <span v-for="d in weekNames" :key="d">{{ getWeekdayName(d) }}</span>
          </div>
          <div class="days">
            <span v-for="n in firstDayOfWeek" :key="'empty' + n" class="empty"></span>
            <span v-for="d in daysInMonth" :key="d" :class="[
              'day',
              { today: isToday(viewYear, viewMonth, d) },
              { 'start-date': isStartDate(viewYear, viewMonth, d) },
              { 'end-date': isEndDate(viewYear, viewMonth, d) },
              { 'in-range': isInRange(viewYear, viewMonth, d) },
              { disabled: isFutureDate(viewYear, viewMonth, d) }
            ]" @click="selectDate(d)">{{ d }}</span>
          </div>
        </div>

        <div class="panel-footer">
          <button class="cancel-btn" @click="showDatePicker = false">{{ $t('performance.cancel') }}</button>
          <button class="ok-btn" @click="confirmDateRange">{{ $t('performance.done') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onActivated } from 'vue'
import { useRouter } from 'vue-router'
import { getPromoterRecordPage } from '@/api/game'
import { http } from '@/utils/request'
import { useI18n } from 'vue-i18n'
import { formatDate } from '@/utils/date'

const { t } = useI18n()

const today = new Date()
const startDate = ref(null) // 默认不设置开始时间
const endDate = ref(null)   // 默认不设置结束时间
const showDatePicker = ref(false)
const showMonthSelect = ref(false)
const isSelectingEndDate = ref(false) // 是否正在选择结束日期
const viewYear = ref(today.getFullYear())
const viewMonth = ref(today.getMonth())
const tempDay = ref(today.getDate())
const router = useRouter()
const containerRef = ref(null)

// 分页参数
const pageNo = ref(1)
const pageSize = ref(10)
const loading = ref(false)
const hasMore = ref(true)

function resetToToday() {
  const now = new Date()
  // 默认不设置时间范围
  startDate.value = null
  endDate.value = null
  viewYear.value = now.getFullYear()
  viewMonth.value = now.getMonth()
  tempDay.value = now.getDate()
}

// 重置分页
function resetPagination() {
  pageNo.value = 1
  hasMore.value = true
  tableData.value = []
}

// 页面首次加载
onMounted(() => {
  resetToToday()
  resetPagination()
  fetchTableData()
})

// 如果用了 keep-alive，页面每次激活也重置
onActivated(() => {
  resetToToday()
  resetPagination()
  fetchTableData()
})

const searchId = ref('')
const tableData = ref([]) // 表格数据

// 滚动处理函数
function handleScroll(event: Event) {
  const target = event.target as HTMLElement
  const scrollTop = target.scrollTop
  const scrollHeight = target.scrollHeight
  const clientHeight = target.clientHeight

  // 当滚动到距离底部100px时触发加载
  if (scrollHeight - scrollTop - clientHeight < 100 && !loading.value && hasMore.value) {
    loadMore()
  }
}

// 加载更多数据
async function loadMore() {
  if (loading.value || !hasMore.value) return

  pageNo.value++
  await fetchTableData(true) // true表示追加数据
}

// 获取表格数据
async function fetchTableData(isAppend = false) {
  if (loading.value) return

  loading.value = true
  try {
    // 构建基础参数
    const baseParams = `pageNo=${pageNo.value}&pageSize=${pageSize.value}&offset=0`

    let url = `/center/user-promoter/getCommissionReward?${baseParams}`

    // 只有当用户选择了时间范围时才添加时间参数
    if (startDate.value && endDate.value) {
      const startTime = formatStartDateTime(startDate.value).replace(/ /g, '%20')
      const endTime = formatEndDateTime(endDate.value).replace(/ /g, '%20')
      url += `&time%5B0%5D=${startTime}&time%5B1%5D=${endTime}`
    }

    console.log('请求URL:', url) // 添加调试日志

    // 直接使用 http.get 发送请求，不传递 params 参数
    const response = await http.get(url)
    if (response && response.data) {
      const newData = response.data.list || []

      if (isAppend) {
        // 追加数据
        tableData.value = [...tableData.value, ...newData]
      } else {
        // 替换数据
        tableData.value = newData
      }

      // 判断是否还有更多数据
      hasMore.value = newData.length === pageSize.value
    }
  } catch (error) {
    console.error('获取数据失败:', error)
    console.error('错误详情:', error.response?.data) // 添加错误详情
    if (!isAppend) {
      tableData.value = []
    }
  } finally {
    loading.value = false
  }
}

// 清空日期范围
function clearDateRange() {
  startDate.value = null
  endDate.value = null
  // 重置分页并重新获取数据
  resetPagination()
  fetchTableData()
}

// 格式化开始日期时间为字符串格式
function formatStartDateTime(date: Date) {
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d} 00:00:00`
}

// 格式化结束日期时间为字符串格式
function formatEndDateTime(date: Date) {
  const y = date.getFullYear()
  const m = (date.getMonth() + 1).toString().padStart(2, '0')
  const d = date.getDate().toString().padStart(2, '0')
  return `${y}-${m}-${d} 23:59:59`
}

// 月份名称数组（使用翻译）
const monthNames = computed(() => [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
])

// 星期名称数组（使用翻译）
const weekNames = computed(() => ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'])

// 获取月份名称
function getMonthName(monthIndex: number) {
  return t(`performance.months.${monthNames.value[monthIndex]}`)
}

// 获取星期名称
function getWeekdayName(weekday: string) {
  return t(`performance.weekdays.${weekday}`)
}

const daysInMonth = computed(() => {
  return new Date(viewYear.value, viewMonth.value + 1, 0).getDate()
})
const firstDayOfWeek = computed(() => {
  return new Date(viewYear.value, viewMonth.value, 1).getDay()
})

function formatDateRange() {
  if (!startDate.value || !endDate.value) {
    return t('performance.selectDateRange') // 使用翻译
  }
  return `${formatDate(startDate.value)} ~ ${formatDate(endDate.value)}`
}

function isToday(y: number, m: number, d: number) {
  return y === today.getFullYear() && m === today.getMonth() && d === today.getDate()
}

function isStartDate(y: number, m: number, d: number) {
  if (!startDate.value) return false
  return y === startDate.value.getFullYear() && m === startDate.value.getMonth() && d === startDate.value.getDate()
}

function isEndDate(y: number, m: number, d: number) {
  if (!endDate.value) return false
  return y === endDate.value.getFullYear() && m === endDate.value.getMonth() && d === endDate.value.getDate()
}

function isInRange(y: number, m: number, d: number) {
  if (!startDate.value || !endDate.value) return false
  const currentDate = new Date(y, m, d)
  // 只有当开始日期和结束日期都存在且不同时，才显示范围
  if (startDate.value.getTime() !== endDate.value.getTime()) {
    return currentDate > startDate.value && currentDate < endDate.value
  }
  return false
}

function isFutureDate(y: number, m: number, d: number) {
  if (y > today.getFullYear()) return true
  if (y === today.getFullYear() && m > today.getMonth()) return true
  if (y === today.getFullYear() && m === today.getMonth() && d > today.getDate()) return true
  return false
}

function isFutureMonth(y: number, m: number) {
  if (y > today.getFullYear()) return true
  if (y === today.getFullYear() && m > today.getMonth()) return true
  return false
}

function prevMonth() {
  if (viewMonth.value === 0) {
    viewYear.value--
    viewMonth.value = 11
  } else {
    viewMonth.value--
  }
  // 不能切到未来
  if (viewYear.value > today.getFullYear() || (viewYear.value === today.getFullYear() && viewMonth.value > today.getMonth())) {
    viewYear.value = today.getFullYear()
    viewMonth.value = today.getMonth()
  }
}

function nextMonth() {
  if (viewYear.value === today.getFullYear() && viewMonth.value === today.getMonth()) return
  if (viewMonth.value === 11) {
    viewYear.value++
    viewMonth.value = 0
  } else {
    viewMonth.value++
  }
  // 不能切到未来
  if (viewYear.value > today.getFullYear() || (viewYear.value === today.getFullYear() && viewMonth.value > today.getMonth())) {
    viewYear.value = today.getFullYear()
    viewMonth.value = today.getMonth()
  }
}

const isPrevDisabled = computed(() => false)
const isNextDisabled = computed(() => viewYear.value === today.getFullYear() && viewMonth.value === today.getMonth())

function selectDate(d: number) {
  if (isFutureDate(viewYear.value, viewMonth.value, d)) return
  tempDay.value = d

  const selectedDate = new Date(viewYear.value, viewMonth.value, d)

  if (isSelectingEndDate.value) {
    // 选择结束日期
    if (startDate.value && selectedDate >= startDate.value) {
      endDate.value = selectedDate
    } else if (startDate.value) {
      // 如果结束日期早于开始日期，交换它们
      endDate.value = startDate.value
      startDate.value = selectedDate
    } else {
      // 如果还没有开始日期，先设置开始日期
      startDate.value = selectedDate
    }
  } else {
    // 选择开始日期
    if (endDate.value && selectedDate <= endDate.value) {
      startDate.value = selectedDate
    } else if (endDate.value) {
      // 如果开始日期晚于结束日期，交换它们
      startDate.value = endDate.value
      endDate.value = selectedDate
    } else {
      // 如果还没有结束日期，先设置开始日期
      startDate.value = selectedDate
    }
  }
}

// 切换到开始日期选择模式
function switchToStartDate() {
  isSelectingEndDate.value = false
  // 清除临时选择状态
  if (startDate.value) {
    tempDay.value = startDate.value.getDate()
    viewYear.value = startDate.value.getFullYear()
    viewMonth.value = startDate.value.getMonth()
  } else {
    // 如果没有开始日期，重置到今天
    const now = new Date()
    tempDay.value = now.getDate()
    viewYear.value = now.getFullYear()
    viewMonth.value = now.getMonth()
  }
}

// 切换到结束日期选择模式
function switchToEndDate() {
  isSelectingEndDate.value = true
  // 清除临时选择状态
  if (endDate.value) {
    tempDay.value = endDate.value.getDate()
    viewYear.value = endDate.value.getFullYear()
    viewMonth.value = endDate.value.getMonth()
  } else {
    // 如果没有结束日期，重置到今天
    const now = new Date()
    tempDay.value = now.getDate()
    viewYear.value = now.getFullYear()
    viewMonth.value = now.getMonth()
  }
}

function confirmDateRange() {
  // 根据当前选择模式设置对应的日期
  const selectedDate = new Date(viewYear.value, viewMonth.value, tempDay.value)

  if (isSelectingEndDate.value) {
    // 选择结束日期
    if (startDate.value) {
      // 如果已经有开始日期，设置结束日期
      if (selectedDate >= startDate.value) {
        endDate.value = selectedDate
      } else {
        // 如果结束日期早于开始日期，交换它们
        endDate.value = startDate.value
        startDate.value = selectedDate
      }
    } else {
      // 如果还没有开始日期，先设置开始日期，不设置结束日期
      startDate.value = selectedDate
      // 切换到开始日期选择模式，让用户继续选择结束日期
      isSelectingEndDate.value = false
      return // 不关闭弹窗，让用户继续选择
    }
  } else {
    // 选择开始日期
    if (endDate.value) {
      // 如果已经有结束日期，设置开始日期
      if (selectedDate <= endDate.value) {
        startDate.value = selectedDate
      } else {
        // 如果开始日期晚于结束日期，交换它们
        startDate.value = endDate.value
        endDate.value = selectedDate
      }
    } else {
      // 如果还没有结束日期，先设置开始日期
      startDate.value = selectedDate
      // 切换到结束日期选择模式，让用户继续选择结束日期
      isSelectingEndDate.value = true
      return // 不关闭弹窗，让用户继续选择
    }
  }

  showDatePicker.value = false
  // 重置分页并重新获取数据
  resetPagination()
  fetchTableData()
}

function selectMonth(m: number) {
  if (isFutureMonth(viewYear.value, m)) return
  viewMonth.value = m
  showMonthSelect.value = false
  // 选完月后，天数要校验
  if (viewYear.value === today.getFullYear() && viewMonth.value === today.getMonth() && tempDay.value > today.getDate()) {
    tempDay.value = today.getDate()
  }
}

function onSearch() {
  // 重置分页并重新获取数据
  resetPagination()
  fetchTableData()
}

// 分页处理
function handlePageChange(newPage: number) {
  pageNo.value = newPage
  fetchTableData()
}

// 格式化表格显示的时间
function formatTableTime(time: string | null) {
  if (!time) return '-'
  // 如果时间格式是 ISO 格式，转换为显示格式
  const date = new Date(time)
  return formatDate(date)
}

// 获取类型文本（使用翻译）
function getTypeText(type: number) {
  switch (type) {
    case 2:
      return t('performance.gameTypes.commission')
    case 1:
      return t('performance.gameTypes.treasureChest')
    default:
      return t('performance.gameTypes.unknown')
  }
}

// 格式化周期显示
function formatPeriod(startTime: string | null, endTime: string | null) {
  if (!startTime && !endTime) return '-'

  const start = startTime ? formatDate(new Date(startTime)) : '-'
  const end = endTime ? formatDate(new Date(endTime)) : '-'

  // 如果只有开始时间，只显示开始时间
  if (startTime && !endTime) {
    return start
  }

  // 如果只有结束时间，只显示结束时间
  if (!startTime && endTime) {
    return end
  }

  // 如果两个时间都存在，显示范围
  return `${start} - ${end}`
}

const handleClick = (row: any) => {
  // 格式化时间，去掉时分秒
  const startTime = row.startTime ? formatDate(new Date(row.startTime)) : ''
  const endTime = row.endTime ? formatDate(new Date(row.endTime)) : ''

  router.push({
    path: '/casseindex', // 使用正确的路由路径
    query: {
      startTime: startTime,
      endTime: endTime
    }
  })
}
</script>

<style scoped>
.performance-container {

  min-height: 100vh;
  padding: 1px 0 0 0;
  border-radius: 8px 8px 0 0;
  overflow-y: auto;
  height: 100vh;
}

.search-bar {
  display: flex;
  align-items: center;
  background: #104939;
  border-radius: 8px 8px 0 0;
  padding: 8px;
  gap: 8px;
}

.date-range-picker {
  display: flex;
  align-items: center;
  background: #004D37;
  border-radius: 6px;
  padding: 6px 5px;
  color: #5FB589;
  font-size: 12px;
  cursor: pointer;
  min-width: 150px;
}

/* 清空按钮样式 */
.clear-date-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #004D37;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.clear-date-btn:hover {
  background: #18553a;
}

/* 模式切换按钮 */
.mode-switch {
  display: flex;
  background: #18553a;
  border-radius: 6px;
  margin: 10px 18px 0 18px;
  padding: 2px;
}

.mode-btn {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  color: #b6e2d3;
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.mode-btn.active {
  background: #1ec98b;
  color: #fff;
  font-weight: bold;
}

.id-search-box {
  display: flex;
  align-items: center;
  background: #004D37;
  border-radius: 10px;
  padding: 0 12px;
  flex: 1;
  height: 28px;
}

.id-input {
  background: transparent;
  border: none;
  outline: none;
  color: #5FB589;
  font-size: 12px;
  padding: 0;
}

.id-input::placeholder {
  color: #5FB589;
  opacity: 0.7;
}

.search-icon {
  width: 11px;
  height: 11px;
  cursor: pointer;
  margin-left: 34px;
  transition: opacity 0.2s;
}

.search-icon:hover {
  opacity: 1;
}

.table-wrap {

  overflow: hidden;
}

.table-header {
  display: flex;
  background: #104939;
  color: #fff;
  align-items: center;
  font-size: 11px;
  padding: 10px 0 8px 0;
}

.th {
  flex: 1;
  text-align: center;
}

.table-row {
  display: flex;
  background: #18553a;
  color: #52A27C;
  font-size: 13px;
  padding: 14px 0;
  align-items: center;
  transition: background 0.2s;
}

.table-row.zebra {
  background: #176343;
}

.td {
  flex: 1;
  text-align: center;
  font-size: 11px;
}

.empty-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 115px 0 0 0;
}

.empty-text {
  color: #ADB1B0;
  font-size: 11px;
  margin-top: 21px;

}

/* 日期选择弹窗 */
.date-picker-modal {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.12);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.date-picker-panel {
  background: #066147;
  border-radius: 8px;
  margin-top: 40px;
  min-width: 300px;
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.12);
  padding: 0 0 10px 0;
  position: relative;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 18px 0 18px;
}

.month-year {
  color: #5FB589;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.panel-arrows svg {
  margin-left: 10px;
  cursor: pointer;
  vertical-align: middle;
}

.panel-arrows svg.disabled {
  opacity: 0.3;
  pointer-events: none;
}

.month-select {
  padding: 0 0 0 0;
}

.month-select-header {
  color: #5FB589;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  width: 100%;
  padding: 6px 0;
  justify-content: center;
}

.month-list {
  display: flex;
  flex-direction: column;
  max-height: 160px;
  overflow-y: auto;
  width: 100%;
  padding: 0;
}

.month-item {
  width: 100%;
  text-align: center;
  padding: 6px 0;
  color: #b6e2d3;
  font-size: 13px;
  cursor: pointer;
  border-radius: 0;
  transition: background 0.15s;
  background: transparent;
}

.month-item.active {
  color: #fff;
  font-weight: bold;
  background: #18553a;
}

.month-item.disabled {
  color: #5FB589;
  opacity: 0.4;
  pointer-events: none;
}

.calendar {
  padding: 0 18px;
}

.weekdays {
  display: flex;
  justify-content: space-between;
  color: #5FB589;
  font-size: 13px;
  margin: 10px 0 0 0;
}

.days {
  display: flex;
  flex-wrap: wrap;
  margin-top: 6px;
}

.empty {
  width: 14.2%;
  height: 32px;
}

.day {
  width: 14.2%;
  height: 32px;
  line-height: 32px;
  text-align: center;
  border-radius: 50%;
  color: #b6e2d3;
  font-size: 15px;
  margin-bottom: 2px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.day.today {
  /* border: 1.5px solid #1ec98b; */
}

.day.start-date {
  background: #1ec98b;
  color: #fff;
  font-weight: bold;
}

.day.end-date {
  background: #1ec98b;
  color: #fff;
  font-weight: bold;
}

.day.in-range {
  background: rgba(30, 201, 139, 0.3);
  color: #fff;
}

.day.disabled {
  color: #5FB589;
  opacity: 0.3;
  pointer-events: none;
}

.panel-footer {
  display: flex;
  justify-content: space-between;
  padding: 18px 18px 0 18px;
  gap: 10px;
}

.cancel-btn,
.ok-btn {
  flex: 1;
  background: none;
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  padding: 10px 0;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.cancel-btn:hover,
.ok-btn:hover {
  background: #18553a;
}

.clickable {
  cursor: pointer;
}
</style>
